import { useCallback, useMemo, type ReactElement } from "react";
import { useLocalization } from "@fluent/react";
import { MultiSelect, TruncatedAccount } from "@narmi/design_system";
import Account from "byzantine/src/Account";

export interface MultiAccountSelectProps {
  id: string;
  label?: string;
  accounts: Account[];
  selectedAccountIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  isDestination?: boolean;
  error?: string;
  errorText?: string;
}

export default function MultiAccountSelect({
  id,
  label,
  accounts,
  selectedAccountIds,
  onSelectionChange,
  isDestination = false,
  error,
  errorText,
}: MultiAccountSelectProps) {
  const { l10n } = useLocalization();

  const accountsLabel =
    label || l10n.getString("label-accounts-selector", {}, "Accounts");
  // Memoize the name so it only recalculates when label changes.
  const name = useMemo(() => `multi-account-selector-${id}`, []);

  // Only depend on accounts.length (and l10n) since that's all that matters here.
  const summaryFormatter = useCallback(
    ({ selectedItems }: { selectedItems: ReactElement[] }) => {
      if (selectedItems.length === 0) {
        return accountsLabel;
      }

      if (selectedItems.length === accounts.length) {
        return l10n.getString(
          "label-accounts-selector-all-selected",
          {},
          "All accounts selected",
        );
      }

      const numAccounts = selectedItems.length;
      const accountDefaultLabel = numAccounts === 1 ? "account" : "accounts";

      return l10n.getString(
        "label-accounts-selector-total",
        {
          numAccounts,
        },
        `${numAccounts} ${accountDefaultLabel} selected`,
      );
    },
    [],
  );

  // Memoize the rendered accounts to avoid recreating them on every render.
  const renderedAccounts = useMemo(
    () =>
      accounts.map((account) => {
        const { name: accountName, endingValue } =
          account.getTruncatedValues(isDestination);
        return (
          <MultiSelect.Item key={account.id} value={account.id}>
            <TruncatedAccount name={accountName} lastFour={endingValue} />
          </MultiSelect.Item>
        );
      }),
    [accounts],
  );

  return (
    <MultiSelect
      name={name}
      label={accountsLabel}
      selectedItems={selectedAccountIds}
      onSelectedItemsChange={onSelectionChange}
      errorText={errorText || error}
      summaryFormatter={summaryFormatter}
      isClearable
    >
      {renderedAccounts}
    </MultiSelect>
  );
}
